import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { feedbackService } from '../services/feedbackService';
import { ErrorHandler } from '@/lib/errors/errorHandler';
import type { CreateFeedbackRequest } from '../types';

/**
 * DOCU: React Query hook for fetching the active feedback categories. <br>
 * Triggered: When the feedback dialog needs its category options. <br>
 * Last Updated Date: July 16, 2026
 * @param enabled - Whether the query should run (e.g. only when the dialog is open)
 * @returns UseQueryResult with the list of active categories
 * @author Jette
 */
export const useFeedbackCategories = (enabled = true) => {
  return useQuery({
    queryKey: ['feedback', 'categories'],
    queryFn: () => feedbackService.getCategories(),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * DOCU: React Query mutation hook for submitting user/client feedback. <br>
 * Triggered: When the user submits the feedback form in FeedbackDialog. <br>
 * Last Updated Date: July 16, 2026
 * @param options - Optional onSuccess callback (e.g. to close the dialog)
 * @returns UseMutationResult with a mutate function for submitting feedback
 * @author Jette
 */
export const useSubmitFeedback = (options?: { onSuccess?: () => void }) => {
  return useMutation({
    mutationFn: (data: CreateFeedbackRequest) => feedbackService.submitFeedback(data),
    onSuccess: () => {
      toast.success('Thanks for your feedback!');
      options?.onSuccess?.();
    },
    onError: (error) => {
      ErrorHandler.toast(error, 'Failed to submit feedback');
    },
  });
};
